/**
 * 从 DataSetInheritContext 解析 dataSet / dataSetField / sessionId（无 React）。
 * 与 useResolvedDataSet 历史行为一致，便于单测与单一维护入口。
 *
 * 注：入参类型与 DataSetInheritContextValue 同构，本文件不 import 该模块，避免与 hook 文件循环依赖。
 */

/** 与 DataSetInheritContextValue 结构一致 */
export type InheritContextForResolve = {
  dataSet: string;
  id?: string | number;
  index?: number;
  parentIndex?: number;
  parentContext?: InheritContextForResolve | null;
  sessionId?: string;
  dataSetFieldRoot?: string;
};

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim() !== "";
}

function normalizeNestedRawField(fieldRoot: string | undefined, rawField: string): string {
  if (fieldRoot === "config.cells" && rawField.startsWith("config.content.")) {
    return `config.${rawField.slice("config.content.".length)}`;
  }
  return rawField;
}

function isAbsoluteArrayPath(path: string | undefined): boolean {
  return isNonEmptyString(path) && path.trim().startsWith("[");
}

function isFieldRootAnchoredAtTemplateItems(path: string | undefined): boolean {
  return isNonEmptyString(path) && /^items\[\d+\]/.test(path.trim());
}

/** fieldRoot 已含 `[n]` 下标（如 `orGroups[0].items[1]`）时不再叠加 parentIndex/index */
function isBracketIndexedFieldRoot(path: string | undefined): boolean {
  return isNonEmptyString(path) && /\[\d+\]/.test(path.trim());
}

/**
 * Masonry 等内层会替换 fieldRoot（如 config.columns），但 parentContext 仍保留 DC 的 items 等段名。
 * 此时应用 parentContext.dataSetFieldRoot[parentIndex].fieldRoot，而非 [parentIndex].fieldRoot。
 */
function buildParentIndexedFieldRootPrefix(
  parentIndex: number,
  fieldRoot: string,
  parentContext: InheritContextForResolve | null | undefined
): string {
  const trimmedRoot = fieldRoot.trim();
  const parentFieldRoot = parentContext?.dataSetFieldRoot?.trim();
  if (
    isNonEmptyString(parentFieldRoot) &&
    !isBracketIndexedFieldRoot(parentFieldRoot) &&
    !isFieldRootAnchoredAtTemplateItems(parentFieldRoot) &&
    parentFieldRoot !== trimmedRoot
  ) {
    return `${parentFieldRoot}[${parentIndex}].${trimmedRoot}`;
  }
  return `[${parentIndex}].${trimmedRoot}`;
}

export type ResolvedInheritedDataSet = {
  dataSet: string | undefined;
  dataSetField: string | undefined;
  sessionId: string | undefined;
};

/**
 * 当组件显式传了 `dataSet` 时，优先使用显式值；
 * 否则从 `ctx` 继承，并把 `dataSetField` 前缀成 `[index].*` 等规则。
 * `ctx` 为 null 时等价于无 Provider。
 */
export function resolveInheritedDataSet(
  propsDataSet: string | undefined,
  propsDataSetField: string | undefined,
  propsSessionId: string | undefined,
  ctx: InheritContextForResolve | null
): ResolvedInheritedDataSet {
  const effectiveSessionId = propsSessionId ?? ctx?.sessionId;

  if (isNonEmptyString(propsDataSet)) {
    return {
      dataSet: propsDataSet,
      dataSetField: propsDataSetField,
      sessionId: effectiveSessionId,
    };
  }

  if (!ctx) {
    return {
      dataSet: propsDataSet,
      dataSetField: propsDataSetField,
      sessionId: effectiveSessionId,
    };
  }

  const index = typeof ctx.index === "number" ? ctx.index : undefined;
  const parentIndex =
    typeof ctx.parentIndex === "number" ? ctx.parentIndex : undefined;
  const baseDataSet = ctx.dataSet;
  const fieldRoot = ctx.dataSetFieldRoot;

  if (isNonEmptyString(propsDataSetField)) {
    const rawField = normalizeNestedRawField(
      fieldRoot,
      propsDataSetField.trim()
    );

    if (rawField.startsWith("[")) {
      return {
        dataSet: baseDataSet,
        dataSetField: isNonEmptyString(fieldRoot) ? `${fieldRoot}${rawField}` : rawField,
        sessionId: effectiveSessionId,
      };
    }

    if (isBracketIndexedFieldRoot(fieldRoot)) {
      return {
        dataSet: baseDataSet,
        dataSetField: `${fieldRoot!.trim()}.${rawField}`,
        sessionId: effectiveSessionId,
      };
    }

    if (
      typeof parentIndex === "number" &&
      typeof index === "number" &&
      isNonEmptyString(fieldRoot) &&
      !isAbsoluteArrayPath(fieldRoot) &&
      !isFieldRootAnchoredAtTemplateItems(fieldRoot)
    ) {
      const prefix = buildParentIndexedFieldRootPrefix(
        parentIndex,
        fieldRoot,
        ctx.parentContext
      );
      const dataSetField = `${prefix}[${index}].${rawField}`;
      return {
        dataSet: baseDataSet,
        dataSetField,
        sessionId: effectiveSessionId,
      };
    }

    if (typeof index === "number") {
      if (isNonEmptyString(fieldRoot)) {
        return {
          dataSet: baseDataSet,
          dataSetField: `${fieldRoot}[${index}].${rawField}`,
          sessionId: effectiveSessionId,
        };
      }
      return {
        dataSet: baseDataSet,
        dataSetField: `[${index}].${rawField}`,
        sessionId: effectiveSessionId,
      };
    }

    const resolvedField = isNonEmptyString(fieldRoot) ? `${fieldRoot}.${rawField}` : rawField;
    return {
      dataSet: baseDataSet,
      dataSetField: resolvedField,
      sessionId: effectiveSessionId,
    };
  }

  if (
    typeof parentIndex === "number" &&
    typeof index === "number" &&
    isNonEmptyString(fieldRoot) &&
    !isAbsoluteArrayPath(fieldRoot) &&
    !isFieldRootAnchoredAtTemplateItems(fieldRoot)
  ) {
    const prefix = buildParentIndexedFieldRootPrefix(
      parentIndex,
      fieldRoot,
      ctx.parentContext
    );
    return {
      dataSet: baseDataSet,
      dataSetField: `${prefix}[${index}]`,
      sessionId: effectiveSessionId,
    };
  }

  if (typeof index === "number" && isNonEmptyString(fieldRoot)) {
    return {
      dataSet: baseDataSet,
      dataSetField: `${fieldRoot}[${index}]`,
      sessionId: effectiveSessionId,
    };
  }

  if (typeof index === "number") {
    return {
      dataSet: baseDataSet,
      dataSetField: `[${index}]`,
      sessionId: effectiveSessionId,
    };
  }

  return {
    dataSet: baseDataSet,
    dataSetField: isNonEmptyString(fieldRoot) ? fieldRoot : propsDataSetField,
    sessionId: effectiveSessionId,
  };
}
